import { useQuery } from '@tanstack/react-query'
import Select from 'react-select'

import { getTools } from '~/generated/client'

// Types
interface Action {
  id: string
  name: string
  description?: string
}

interface ActionSelectorProps {
  appName: string
  limitedActions: string[] | undefined
  setLimitedActions: (limitedActions: string[]) => void
}

/**
 * Action Selector Section
 *
 * This section allows users to select multiple actions from the available
 * actions for a given app. It fetches the actions list based on the provided
 * app name and displays them in a searchable multi-select dropdown.
 * The selector is initially locked and must be unlocked to make selections.
 */
export const ActionSelector = ({
  appName,
  limitedActions,
  setLimitedActions,
}: ActionSelectorProps) => {
  // Data fetching
  const { data: actions, isLoading } = useQuery<Action[]>(
    ['connector-actions', appName],
    async () => {
      if (!appName) return [] as Action[]
      const response = await getTools({
        toolkitSlug: appName,
        limit: '9999999',
      })
      return response.items.map((item) => ({
        id: item.slug,
        name: item.name,
      }))
    },
    {
      enabled: !!appName,
    },
  )

  // Transform actions into select options
  const options =
    actions?.map((action) => ({
      value: action.id,
      label: action.name,
    })) || []

  const selectedOptions = options.filter((option) =>
    limitedActions?.includes(option.value),
  )

  // Render select component
  return (
    <div className="action-selector-section">
      <div className="mb-1 flex items-center justify-between">
        <div className="font-cera text-[15px] font-[500] text-black-400">
          Limit Actions to
        </div>
      </div>

      <style>
        {`
                    .react-select__input {
                        box-shadow: none !important;
                        border-color: transparent !important;
                        outline: none !important;
                    }
                    `}
      </style>
      <Select<{ value: string; label: string }, true>
        value={selectedOptions}
        onChange={(options) =>
          setLimitedActions(options?.map((opt) => opt.value) || [])
        }
        options={options}
        isLoading={isLoading}
        placeholder="Select actions..."
        className="text-black w-full"
        styles={{
          menuList: (base) => ({
            ...base,
            color: 'black',
          }),
          menu: (base) => ({
            ...base,
            position: 'absolute',
            zIndex: 9999,
          }),
        }}
        classNamePrefix="react-select"
        isMulti
      />
    </div>
  )
}
